import { HCDatepicker } from '@/libs/components/src/lib/datepicker/HCDatepicker';
import { QuestionAnswer } from '@chiroup/core/types/ComplaintSummary.type';
import { DateValue } from '@chiroup/core/types/Survey.type';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

interface HCDateProps {
  questionId: string;
  questionData: QuestionAnswer;
  onAnswerChange: (questionId: string, newValue: string | string[], otherValue?: string) => void;
}

const HCDate: React.FC<HCDateProps> = ({ questionId, questionData, onAnswerChange }) => {
  const { selectedValues = [] } = questionData;
  const [currentDateValue, setCurrentDateValue] = useState<DateValue | undefined>();

  // Parse the current value to determine if it's exact or estimate
  const currentValue = selectedValues[0];

  useEffect(() => {
    if (currentValue) {
      try {
        // Try to parse as JSON first (for complex date objects)
        const parsed = typeof currentValue === 'string' ? JSON.parse(currentValue) : currentValue;

        if (parsed.exact) {
          setCurrentDateValue({ exact: parsed.exact });
        } else if (parsed.estimate) {
          setCurrentDateValue({ estimate: parsed.estimate });
        }

        return;
      } catch {
        // Not JSON, continue with string parsing
      }

      // Handle string values
      if (typeof currentValue === 'string') {
        if (currentValue.includes(' ago')) {
          // Parse formatted estimate string like "1 week ago" or "3 days ago"
          const match = currentValue.match(/(\d+)\s+(day|week|month|year)s?\s+ago/i);

          if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2].toLowerCase() as 'day' | 'week' | 'month' | 'year';
            setCurrentDateValue({ estimate: { value, unit } });
          } else {
            setCurrentDateValue(undefined);
          }
        } else {
          // Try to parse as a date string (MM/DD/YYYY or formatted like "January 15, 2025")
          const parsed = dayjs(currentValue);

          if (parsed.isValid()) {
            // Convert to MM/DD/YYYY format for the datepicker
            setCurrentDateValue({ exact: parsed.format('MM/DD/YYYY') });
          } else {
            setCurrentDateValue(undefined);
          }
        }
      } else {
        setCurrentDateValue(undefined);
      }
    } else {
      setCurrentDateValue(undefined);
    }
  }, [currentValue]);

  const handleDateChange = (dateValue: DateValue | undefined) => {
    setCurrentDateValue(dateValue);

    if (dateValue) {
      // Format the date value for display
      let formattedValue: string;

      if (dateValue.exact) {
        try {
          const date = dayjs(dateValue.exact, 'MM/DD/YYYY');
          formattedValue = date.format('MMMM D, YYYY');
        } catch {
          formattedValue = dateValue.exact;
        }
      } else if (dateValue.estimate) {
        const { value, unit } = dateValue.estimate;
        const unitText = value === 1 ? unit : `${unit}s`;
        formattedValue = `${value} ${unitText} ago`;
      } else {
        formattedValue = 'Invalid date';
      }

      // Store both the formatted display value and the raw JSON for data integrity
      onAnswerChange(questionId, [formattedValue]);
    } else {
      onAnswerChange(questionId, []);
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <HCDatepicker
        options={{ allowEstimate: true, pastOnly: false, futureOnly: false }}
        onChange={handleDateChange}
        primaryColor="#93cc68"
        language="en"
        size="small"
        hideSelectedMessage={true}
        initialValue={currentDateValue}
        noShadow
      />
    </div>
  );
};

export default HCDate;
